import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Home as HomeIcon,
  Restaurant as FoodIcon,
  MenuBook as RecipeIcon,
  Label as LabelIcon,
} from '@mui/icons-material';

import SuggestedRecipes from './components/SuggestedRecipes';
import { SuggestionDetail } from './suggestions/pages';
import FoodSuggestionsPage from './suggestions/pages/FoodSuggestionsPage';
import FoodLabelSuggestionsPage from './suggestions/pages/FoodLabelSuggestionsPage';

/**
 * Suggestions component - Main router for all suggestion types
 * Uses nested routing with tabs for navigation between suggestion types
 */
export default function Suggestions() {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab based on current route
  const getActiveTab = () => {
    if (location.pathname.includes('/suggestions/foods')) return 0;
    if (location.pathname.includes('/suggestions/recipes')) return 1;
    if (location.pathname.includes('/suggestions/labels')) return 2;
    return 0;
  };

  const handleTabChange = (event, newValue) => {
    switch (newValue) {
      case 0:
        navigate('/suggestions/foods');
        break;
      case 1:
        navigate('/suggestions/recipes');
        break;
      case 2:
        navigate('/suggestions/labels');
        break;
      default:
        navigate('/suggestions/foods');
    }
  };

  // Check if we're on a detail page (don't show tabs)
  const isDetailPage = /\/suggestions\/(foods|labels|recipes)\/\d+/.test(location.pathname);

  return (
    <Box>
      {/* Breadcrumbs - only show on list pages */}
      {!isDetailPage && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Suggestions</Typography>
        </Breadcrumbs>
      )}

      {/* Main Content */}
      {!isDetailPage && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Content Suggestions Management
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Review and manage user-submitted content suggestions across all categories.
          </Typography>

          {/* Tabs for Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={getActiveTab()}
              onChange={handleTabChange}
              aria-label="suggestions tabs"
              variant="fullWidth"
            >
              <Tab
                icon={<FoodIcon />}
                label="Food Suggestions"
                iconPosition="start"
                sx={{ textTransform: 'none', fontSize: '1rem' }}
              />
              <Tab
                icon={<RecipeIcon />}
                label="Recipe Suggestions"
                iconPosition="start"
                sx={{ textTransform: 'none', fontSize: '1rem' }}
              />
              <Tab
                icon={<LabelIcon />}
                label="Food Label Suggestions"
                iconPosition="start"
                sx={{ textTransform: 'none', fontSize: '1rem' }}
              />
            </Tabs>
          </Box>
        </Paper>
      )}

      {/* Nested Routes */}
      <Routes>
        {/* Default redirect to foods */}
        <Route index element={<Navigate to="foods" replace />} />

        {/* Food Suggestions Routes */}
        <Route path="foods" element={<FoodSuggestionsPage />} />
        <Route
          path="foods/:id"
          element={<SuggestionDetail type="food" basePath="/suggestions/foods" />}
        />

        {/* Recipe Suggestions Routes */}
        <Route path="recipes" element={<SuggestedRecipes />} />
        <Route
          path="recipes/:id"
          element={<SuggestionDetail type="recipe" basePath="/suggestions/recipes" />}
        />

        {/* Food Label Suggestions Routes */}
        <Route path="labels" element={<FoodLabelSuggestionsPage />} />
        <Route
          path="labels/:id"
          element={<SuggestionDetail type="foodLabel" basePath="/suggestions/labels" />}
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="foods" replace />} />
      </Routes>
    </Box>
  );
}
